import { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Shield, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getAuditLogs } from "@/lib/api";

const ACTION_TYPES = [
  "login", "logout", "create_customer", "update_customer", "delete_customer",
  "bulk_create_customers", "bulk_delete_customers", "create_bill", "bulk_create_bills",
  "edit_bill", "mark_paid", "bulk_mark_paid", "update_settings", "rate_change",
  "export_data", "export_excel", "restore_data", "create_user", "update_user",
  "reset_password", "change_password",
];

export default function AuditLog() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [action, setAction] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(0);

  const fetchLogs = async () => {
    const params = { skip: page * 50, limit: 50 };
    if (action !== "all") params.action = action;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    const res = await getAuditLogs(params);
    setLogs(res.logs || []);
    setTotal(res.total || 0);
  };

  useEffect(() => { fetchLogs(); }, [action, dateFrom, dateTo, page]);

  const actionColor = (a) => {
    if (a.includes("delete")) return "bg-red-100 text-red-700";
    if (a.includes("create") || a === "login") return "bg-green-100 text-green-700";
    if (a.includes("update") || a.includes("edit") || a.includes("change") || a.includes("reset")) return "bg-blue-100 text-blue-700";
    if (a.includes("export") || a.includes("paid")) return "bg-orange-100 text-orange-700";
    return "bg-zinc-100 text-zinc-700";
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1600px] mx-auto" data-testid="audit-log-page">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="outline" onClick={() => navigate(-1)} className="rounded-sm border-zinc-300"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-zinc-900">
          <Shield className="w-8 h-8 inline mr-2 text-orange-600" />Audit Log
        </h1>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="min-w-[160px]">
          <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1 block">Action</label>
          <Select value={action} onValueChange={(v) => { setAction(v); setPage(0); }}>
            <SelectTrigger data-testid="audit-action-filter" className="bg-zinc-50 border-zinc-300 rounded-sm text-xs h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {ACTION_TYPES.map((a) => <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[140px]">
          <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1 block">From</label>
          <Input data-testid="audit-date-from" type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(0); }} className="bg-zinc-50 border-zinc-300 rounded-sm text-xs h-8" />
        </div>
        <div className="min-w-[140px]">
          <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1 block">To</label>
          <Input data-testid="audit-date-to" type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(0); }} className="bg-zinc-50 border-zinc-300 rounded-sm text-xs h-8" />
        </div>
        <span className="text-xs text-zinc-400 font-mono">{total} entries</span>
      </div>

      <Card className="border border-zinc-200 rounded-sm shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="text-xs font-bold uppercase tracking-wider text-zinc-500">Time</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-zinc-500">User</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-zinc-500">Role</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-zinc-500">Action</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-zinc-500">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-zinc-400 py-12">No audit logs found.</TableCell></TableRow>
              ) : logs.map((log) => (
                <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                  <TableCell className="text-xs font-mono whitespace-nowrap">{new Date(log.timestamp).toLocaleString("en-IN")}</TableCell>
                  <TableCell className="text-sm">{log.userName}</TableCell>
                  <TableCell><Badge className="text-[10px] bg-zinc-100 text-zinc-700">{log.userRole}</Badge></TableCell>
                  <TableCell><Badge className={`text-[10px] ${actionColor(log.action)}`}>{log.action.replace(/_/g, " ")}</Badge></TableCell>
                  <TableCell className="text-xs text-zinc-600 max-w-[400px] truncate">{log.details}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {total > 50 && (
            <div className="flex items-center justify-between p-3 border-t border-zinc-200">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="rounded-sm text-xs">Previous</Button>
              <span className="text-xs text-zinc-500">Page {page + 1} of {Math.ceil(total / 50)}</span>
              <Button variant="outline" size="sm" disabled={(page + 1) * 50 >= total} onClick={() => setPage((p) => p + 1)} className="rounded-sm text-xs">Next</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
